<script setup lang="ts">
/**
 * Product-edit companion for "Replace WooCommerce product image".
 *
 * The replacement image is a composite of THIS product's catalog mockup and the
 * assigned template, rendered in the browser because there is no PHP compositor.
 * This element shows the stored composite, regenerates it on demand, and refreshes
 * it automatically once the template or the catalog product changes underneath it.
 */
import { computed, onMounted, ref } from 'vue';
import { getQuickConfig, saveProductMockup } from './api';
import {
  isProductMockupStale,
  renderAssignedTemplateMockup,
  type ProductMockupState,
  type QuickConfig,
} from './productMockup';

type MockupTone = 'loading' | 'idle' | 'ready' | 'stale' | 'working' | 'success' | 'error';

const props = defineProps<{ wooProductId?: string }>();

const productId = computed(() => Math.max(0, Number(props.wooProductId || 0)));
const loading = ref(true);
const busy = ref(false);
const error = ref('');
const status = ref('');
const mockup = ref<ProductMockupState | null>(null);
const replaceEnabled = ref(false);
let cachedConfig: QuickConfig | null = null;

const stale = computed(() => isProductMockupStale(mockup.value));
const previewUrl = computed(() => mockup.value?.mockup_url || '');

const displayState = computed(() => {
  if (loading.value) {
    return {
      tone: 'loading' as MockupTone,
      label: 'Loading',
      detail: 'Loading product image…',
    };
  }
  if (error.value) {
    return {
      tone: 'error' as MockupTone,
      label: 'Error',
      detail: error.value,
    };
  }
  if (busy.value || status.value === 'Rendering product image...') {
    return {
      tone: 'working' as MockupTone,
      label: 'Working',
      detail: status.value || 'Rendering product image…',
    };
  }
  if (status.value) {
    return {
      tone: 'success' as MockupTone,
      label: 'Updated',
      detail: status.value,
    };
  }
  if (!previewUrl.value) {
    return {
      tone: 'idle' as MockupTone,
      label: 'Not generated',
      detail: 'Storefront still shows the original image.',
    };
  }
  if (stale.value) {
    return {
      tone: 'stale' as MockupTone,
      label: 'Out of date',
      detail: 'Template or catalog product changed.',
    };
  }
  if (!replaceEnabled.value) {
    return {
      tone: 'ready' as MockupTone,
      label: 'Ready',
      detail: 'Enable image replacement to use it.',
    };
  }
  return {
    tone: 'ready' as MockupTone,
    label: 'Current',
    detail: 'Composited from this product’s mockup.',
  };
});

const actionAriaLabel = computed(() =>
  busy.value ? 'Refreshing product image' : 'Refresh product image',
);

async function loadConfig(force = false): Promise<QuickConfig> {
  if (!cachedConfig || force) {
    cachedConfig = (await getQuickConfig<QuickConfig>(productId.value)).config;
  }
  return cachedConfig;
}

async function regenerate(): Promise<void> {
  if (busy.value || productId.value <= 0) return;
  busy.value = true;
  error.value = '';
  status.value = 'Rendering product image...';
  try {
    const config = await loadConfig(true);
    const rendered = await renderAssignedTemplateMockup(config);
    if (!rendered) throw new Error('This template has no visible layers to render.');
    const saved = await saveProductMockup(productId.value, rendered.dataUrl);
    mockup.value = {
      mockup_attachment_id: saved.mockup_attachment_id,
      mockup_url: saved.mockup_url,
      mockup_signature: saved.mockup_signature,
      stored_mockup_signature: saved.stored_mockup_signature,
    };
    status.value = 'Product image updated.';
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Could not generate the product image.';
    status.value = '';
  } finally {
    busy.value = false;
  }
}

onMounted(async () => {
  if (productId.value <= 0) {
    loading.value = false;
    return;
  }
  try {
    const config = await loadConfig();
    replaceEnabled.value = config.replaceProductImage === true;
    mockup.value = config.productMockup ?? null;
    // Only auto-render when the saved settings say this image is actually in use.
    if (replaceEnabled.value && stale.value) {
      await regenerate();
    }
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Could not read the template assignment.';
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div
    v-if="productId > 0"
    class="veyop-template-mockup"
    :class="`veyop-template-mockup--${displayState.tone}`"
  >
    <div class="veyop-template-mockup__preview" aria-hidden="true">
      <img v-if="previewUrl" :src="previewUrl" alt="" />
      <span v-else class="veyop-template-mockup__preview-empty">—</span>
    </div>

    <div class="veyop-template-mockup__body" aria-live="polite">
      <p class="veyop-template-mockup__title">
        <span class="veyop-template-mockup__marker" aria-hidden="true" />
        <span class="veyop-template-mockup__title-text">Product image</span>
        <span class="veyop-template-mockup__state">{{ displayState.label }}</span>
      </p>
      <p class="veyop-template-mockup__detail" :title="displayState.detail">
        {{ displayState.detail }}
      </p>
    </div>

    <button
      type="button"
      class="veyop-template-mockup__action"
      :disabled="busy || loading"
      :aria-label="actionAriaLabel"
      :title="actionAriaLabel"
      :aria-busy="busy ? 'true' : undefined"
      @click="regenerate"
    >
      <svg
        class="veyop-template-mockup__action-icon"
        :class="{ 'veyop-template-mockup__action-icon--spin': busy }"
        viewBox="0 0 24 24"
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    </button>
  </div>
</template>

<!--
  Styles live in ./storefront.tw.css, not here: this custom element is
  registered with `shadowRoot: false` (storefront.ts), and Vue does not apply
  a component's own scoped styles in that mode.
-->

